/**
 * Ace Streamz - Web Application
 * React Native Web implementation with full navigation support
 */

import React, { useEffect, useMemo, useRef, Suspense, lazy } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { NavigationContainer, DefaultTheme, Theme as NavTheme, useNavigationContainerRef, CommonActions } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

// i18n Configuration
import './src/i18n/i18n.config';
import { I18nInitializer } from './src/components/I18nInitializer';

// Contexts
import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import { ThemeProvider, useTheme } from './src/contexts/ThemeContext';
import { PusherProvider } from './src/contexts/PusherContext';
import { LanguageProvider } from './src/contexts/LanguageContext';

// Navigation linking
import linking from './src/navigation/linking';

// Web Layout
import WebLayout from './screens/WebLayout';

// App-level watcher that pops a notice when a platform account's status changes
import PlatformStatusWatcher from './screens/Components/PlatformStatusWatcher';

console.log('[Ace Streamz Web] App module loaded');

// Lazy load screens for code splitting
const splashScreen = lazy(() => import('./screens/splashScreen'));
const welcomeScreen = lazy(() => import('./screens/welcomeScreen'));
const LoginScreen = lazy(() => import('./screens/LoginScreen'));
const SignUpScreen = lazy(() => import('./screens/SignUpScreen'));
const TermsAndConditionsScreen = lazy(() => import('./screens/TermsAndConditions'));
const OtpVerification = lazy(() => import('./screens/OtpVerification'));
const ProfileImgUpload = lazy(() => import('./screens/ProfileImgUpload'));
const ForgotPasswordScreen = lazy(() => import('./screens/ForgotPasswordScreen'));
const ResetPasswordScreen = lazy(() => import('./screens/ResetPasswordScreen'));
const ChangePasswordScreen = lazy(() => import('./screens/ChangePasswordScreen'));

const Dashboard = lazy(() => import('./screens/Dashboard'));
const ExploreScreen = lazy(() => import('./screens/ExploreScreen'));
const NotificationScreen = lazy(() => import('./screens/NotificationScreen'));

const LiveScreen = lazy(() => import('./screens/LiveScreen'));
const GoLiveScreen = lazy(() => import('./screens/GoLiveScreen'));
const StreamingLiveScreen = lazy(() => import('./screens/StreamingLiveScreen'));
const MyLiveStreamingScreen = lazy(() => import('./screens/MyLiveStreamingScreen'));
const WowzaLiveStreamPlayer = lazy(() => import('./screens/VideoPlayer/WowzaLiveStreamPlayer'));
const PlayerScreen = lazy(() => import('./screens/VideoPlayer/PlayerScreen'));
const LiveVideoStreamer = lazy(() => import('./screens/VideoPlayer/LiveVideoStreamer'));

const ChatScreen = lazy(() => import('./screens/ChatScreen'));
const ChatUserScreen = lazy(() => import('./screens/ChatUserScreen'));

const ProfileScreen = lazy(() => import('./screens/ProfileScreen'));
const EditProfileScreen = lazy(() => import('./screens/EditProfileScreen'));
const ViewOtherProfileScreen = lazy(() => import('./screens/ViewOtherProfileScreen'));
const FollowersScreen = lazy(() => import('./screens/FollowersScreen'));
const FollowingScreen = lazy(() => import('./screens/FollowingScreen'));
const PrivacyPolicyScreen = lazy(() => import('./screens/PrivacyPolicyScreen'));

const CoinWalletScreen = lazy(() => import('./screens/CoinWalletScreen'));
const AddCoinScreen = lazy(() => import('./screens/AddCoinScreen'));
const RedeemScreen = lazy(() => import('./screens/RedeemScreen'));
const AddCardScreen = lazy(() => import('./screens/AddCardScreen'));
const SubscriptionScreen = lazy(() => import('./screens/SubscriptionScreen'));
const RedemptionHistoryScreen = lazy(() => import('./screens/RedemptionHistoryScreen'));

const PlatformAccountsScreen = lazy(() => import('./screens/PlatformAccountsScreen'));
const AddPlatformAccountScreen = lazy(() => import('./screens/AddPlatformAccountScreen'));
const PlatformCreditCoinScreen = lazy(() => import('./screens/PlatformCreditCoinScreen'));

const KycWebViewScreen = lazy(() => import('./screens/KycWebViewScreen'));
const CryptoPaymentWebViewScreen = lazy(() => import('./screens/CryptoPaymentWebViewScreen'));

const Stack = createNativeStackNavigator();

// Loading fallback component
const LoadingScreen: React.FC = () => (
  <View style={styles.loadingContainer}>
    <ActivityIndicator size="large" color="#e94560" />
    <Text style={styles.loadingText}>Loading...</Text>
  </View>
);

// Wrap lazy components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<any>) => {
  return (props: any) => (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

// Wrap authenticated screens with WebLayout (shows sidebar on desktop)
const withLayout = (Component: React.LazyExoticComponent<any>) => {
  return (props: any) => (
    <Suspense fallback={<LoadingScreen />}>
      <WebLayout>
        <Component {...props} />
      </WebLayout>
    </Suspense>
  );
};

// Main Navigator Component
function AppNavigator() {
  const { isAuthenticated, isLoading } = useAuth();
  const { theme } = useTheme();
  const navigationRef = useNavigationContainerRef();
  const wasAuthenticated = useRef(isAuthenticated);

  console.log('[Ace Streamz Web] AppNavigator render, isLoading:', isLoading, 'isAuthenticated:', isAuthenticated);

  // On logout (authenticated -> unauthenticated), send the user straight to the
  // login screen. The group has already swapped by the time this effect runs, so
  // LoginScreen is registered and the reset is valid. Without this, the stale
  // in-app URL (e.g. /dashboard) has no match in the unauthenticated group and
  // React Navigation falls back to `splashScreen` — a 3s branding detour.
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated && navigationRef.isReady()) {
      navigationRef.dispatch(
        CommonActions.reset({ index: 0, routes: [{ name: 'LoginScreen' }] })
      );
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, navigationRef]);

  // Create navigation theme based on app theme
  const navTheme: NavTheme = useMemo(
    () => ({
      ...DefaultTheme,
      dark: theme.mode === 'dark',
      colors: {
        ...DefaultTheme.colors,
        background: theme.colors.background,
        primary: theme.colors.primary,
        text: theme.colors.text,
        border: theme.colors.border,
        card: theme.colors.surface,
        notification: '#e94560',
      },
    }),
    [theme]
  );

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
    <NavigationContainer
      ref={navigationRef}
      theme={navTheme}
      linking={linking}
      documentTitle={{
        formatter: (options, route) =>
          `${options?.title ?? route?.name ?? 'Ace Streamz'} - Ace Streamz`,
      }}
    >
      <Stack.Navigator
        initialRouteName={isAuthenticated ? 'Dashboard' : 'splashScreen'}
        screenOptions={{
          headerShown: false,
          animation: 'fade',
          contentStyle: { backgroundColor: theme.colors.background },
        }}
      >
        {!isAuthenticated ? (
          /*
           * Unauthenticated: ONLY auth screens are registered.
           * Any attempt to deep-link to a protected URL (e.g. /dashboard,
           * /wallet) while logged out has no matching screen, so React
           * Navigation falls back to `initialRouteName` (splashScreen ->
           * LoginScreen). This prevents accessing the web app without login.
           */
          <Stack.Group>
            <Stack.Screen name="splashScreen" component={withSuspense(splashScreen)} />
            <Stack.Screen name="welcomeScreen" component={withSuspense(welcomeScreen)} />
            <Stack.Screen name="LoginScreen" component={withSuspense(LoginScreen)} />
            <Stack.Screen name="SignUpScreen" component={withSuspense(SignUpScreen)} />
            <Stack.Screen name="TermsAndConditionsScreen" component={withSuspense(TermsAndConditionsScreen)} />
            <Stack.Screen name="OtpVerification" component={withSuspense(OtpVerification)} />
            <Stack.Screen name="ProfileImgUpload" component={withSuspense(ProfileImgUpload)} />
            <Stack.Screen name="ForgotPasswordScreen" component={withSuspense(ForgotPasswordScreen)} />
            <Stack.Screen name="ResetPasswordScreen" component={withSuspense(ResetPasswordScreen)} />
          </Stack.Group>
        ) : (
          /*
           * Authenticated: protected app screens. Auth-only screens such as
           * Login/SignUp are intentionally NOT registered here, so a logged-in
           * user hitting /login is redirected to the Dashboard instead.
           */
          <Stack.Group>
            {/* Main Screens - with sidebar layout */}
            <Stack.Screen name="Dashboard" component={withLayout(Dashboard)} />
            <Stack.Screen name="ExploreScreen" component={withLayout(ExploreScreen)} />
            <Stack.Screen name="NotificationScreen" component={withLayout(NotificationScreen)} />
            <Stack.Screen name="ChangePasswordScreen" component={withSuspense(ChangePasswordScreen)} />

            {/* Live Streaming - with sidebar layout */}
            <Stack.Screen name="LiveScreen" component={withLayout(LiveScreen)} />
            <Stack.Screen name="GoLiveScreen" component={withLayout(GoLiveScreen)} />
            <Stack.Screen name="StreamingLiveScreen" component={withLayout(StreamingLiveScreen)} />
            <Stack.Screen name="MyLiveStreamingScreen" component={withLayout(MyLiveStreamingScreen)} />
            <Stack.Screen name="WowzaLiveStreamPlayer" component={withLayout(WowzaLiveStreamPlayer)} />
            <Stack.Screen name="PlayerScreen" component={withLayout(PlayerScreen)} />
            <Stack.Screen name="LiveVideoStreamer" component={withLayout(LiveVideoStreamer)} />

            {/* Chat - with sidebar layout */}
            <Stack.Screen name="ChatScreen" component={withLayout(ChatScreen)} />
            <Stack.Screen name="ChatUserScreen" component={withLayout(ChatUserScreen)} />

            {/* Profile - with sidebar layout */}
            <Stack.Screen name="ProfileScreen" component={withLayout(ProfileScreen)} />
            <Stack.Screen name="EditProfileScreen" component={withLayout(EditProfileScreen)} />
            <Stack.Screen name="ViewOtherProfileScreen" component={withLayout(ViewOtherProfileScreen)} />
            <Stack.Screen name="FollowersScreen" component={withLayout(FollowersScreen)} />
            <Stack.Screen name="FollowingScreen" component={withLayout(FollowingScreen)} />
            <Stack.Screen name="PrivacyPolicyScreen" component={withLayout(PrivacyPolicyScreen)} />
            {/* Terms — reachable from the side menu. Registered with withSuspense
                (not withLayout) because the screen wraps its own WebLayout. */}
            <Stack.Screen name="TermsAndConditionsScreen" component={withSuspense(TermsAndConditionsScreen)} />

            {/* Wallet & Payments - with sidebar layout */}
            <Stack.Screen name="CoinWalletScreen" component={withLayout(CoinWalletScreen)} />
            <Stack.Screen name="AddCoinScreen" component={withLayout(AddCoinScreen)} />
            <Stack.Screen name="RedeemScreen" component={withLayout(RedeemScreen)} />
            <Stack.Screen name="AddCardScreen" component={withLayout(AddCardScreen)} />
            <Stack.Screen name="SubscriptionScreen" component={withLayout(SubscriptionScreen)} />
            <Stack.Screen name="RedemptionHistoryScreen" component={withLayout(RedemptionHistoryScreen)} />

            {/* Platform Accounts - with sidebar layout */}
            <Stack.Screen name="PlatformAccountsScreen" component={withLayout(PlatformAccountsScreen)} />
            <Stack.Screen name="AddPlatformAccountScreen" component={withLayout(AddPlatformAccountScreen)} />
            <Stack.Screen name="PlatformCreditCoinScreen" component={withLayout(PlatformCreditCoinScreen)} />

            {/* KYC */}
            <Stack.Screen name="KycWebViewScreen" component={withSuspense(KycWebViewScreen)} />

            {/* Crypto Payment */}
            <Stack.Screen name="CryptoPaymentWebViewScreen" component={withSuspense(CryptoPaymentWebViewScreen)} />
          </Stack.Group>
        )}
      </Stack.Navigator>
    </NavigationContainer>
    {isAuthenticated && <PlatformStatusWatcher />}
    </>
  );
}

// Root App Component
function App() {
  useEffect(() => {
    console.log('[Ace Streamz Web] App mounted');
  }, []);

  // Optional: Configure API endpoint for language fetching
  // Replace with your actual API endpoint
  const languageApiUrl = process.env.REACT_APP_LANGUAGE_API_URL;

  return (
    <GestureHandlerRootView style={styles.root}>
      <View style={styles.appContainer}>
        <I18nInitializer>
          <LanguageProvider apiUrl={languageApiUrl}>
            <ThemeProvider>
              <AuthProvider>
                <PusherProvider>
                  <AppNavigator />
                </PusherProvider>
              </AuthProvider>
            </ThemeProvider>
          </LanguageProvider>
        </I18nInitializer>
      </View>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: '100vh' as any,
    backgroundColor: '#0B0F14',
  },
  appContainer: {
    flex: 1,
    width: '100%',
    backgroundColor: '#0B0F14',
    minHeight: '100vh' as any,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0B0F14',
  },
  loadingText: {
    marginTop: 16,
    color: '#E5E7EB',
    fontSize: 16,
  },
});

export default App;
